// mu — DB module.
//
// Opens <state-dir>/mu.db (or the MU_DB_PATH override; see defaultDbPath),
// enables WAL + foreign keys, applies the schema idempotently, and
// exposes the live Database handle.
//
// Schema (v11 — task substates on the v10 three-state lifecycle):
//   - 6 entity tables: workstreams, agents, tasks, task_edges,
//                      task_notes, vcs_workspaces
//   - 1 ops log:       ops        (the single append-only record of
//                                  every change — VISION.md § 2b)
//   - 2 sync tables:   sync_peers (per-peer watermarks),
//                      sync_fingerprints (caught-up fingerprints)
//   - 2 meta tables:   schema_version, machine_identity
//   - 1 lookup table:  task_substates (legal (status, substate) pairs,
//                                      seeded from code when missing)
//   - 3 views:         ready, blocked, goals
//   => EXPECTED_TABLES is exactly 12 entries.
//
// v11 adds tasks.substate, which qualifies status (OPEN/parked,
// CLOSED/wontfix, ...). A composite FK (status, substate) ->
// task_substates, DEFERRABLE INITIALLY DEFERRED, is the only guard on
// the pair: it is checked at COMMIT, so apply's one-field-at-a-time
// UPDATEs pass as long as the pair is valid when the transaction ends.
//
// v9 is a BREAKING, migration-free redesign. It DROPS v8's four
// separate change-recording mechanisms — `agent_logs`, `snapshots`,
// `workstream_sync`, and the five `archived_*` tables — and replaces
// all of them with one **ops log**. Sync, undo, and history
// are queries or replays over that log (docs/VOCABULARY.md § op,
// ops log, watermark).
//
// The surrogate-INTEGER-PK discipline introduced in v5 is unchanged:
// per docs/architecture/sdk.md § Surrogate-PK and SDK-boundary discipline,
// every entity table has an INTEGER PK; FKs reference INTEGER ids;
// the operator-facing TEXT name is per-scope unique via
// UNIQUE (<scope_id>, <name>). `ops` is the deliberate exception: it
// holds the NATURAL key (`ws/local_id`) in `ops.key`, never a
// surrogate id, which is exactly why keys don't collide across
// machines.
//
// IMPORTANT: MIN_ACCEPTED_SCHEMA_VERSION === CURRENT_SCHEMA_VERSION
// === 11. There is no in-place forward-bump ladder: every pre-v11 DB
// is rejected at openDb time with SchemaTooOldError (exit 4), and a DB
// newer than this build with SchemaTooNewError (exit 4). Migration
// lives only in scripts/migrate.ts.

import { randomUUID } from "node:crypto";
import { mkdirSync } from "node:fs";
import { homedir, hostname } from "node:os";
import { dirname, join, resolve } from "node:path";
import Database, { type Database as DatabaseType, type Statement } from "better-sqlite3";
import { installCapture } from "./capture.js";
import type { HasNextSteps, NextStep } from "./output.js";
import { defaultStateDir, xdgStateHome } from "./state-dir.js";
import { TASK_SUBSTATE_ROWS } from "./tasks/status.js";

export { defaultStateDir };

export type Db = DatabaseType;

const statementCache = new WeakMap<Db, Map<string, Statement>>();

/**
 * `db.prepare(sql)`, compiled once per connection and reused.
 *
 * better-sqlite3 has no statement cache, so a hot loop that calls
 * `db.prepare` per row pays SQL compilation every time. Ingest applies
 * thousands of ops and prepared ~10 statements per op, which made SQL
 * compilation most of a large catch-up's cost. Use this on per-op paths;
 * one-off queries keep plain `db.prepare`. `sql` must come from a fixed
 * template (the cache never evicts). Not for a statement a caller
 * `.iterate()`s while re-entering the same SQL: a busy statement throws.
 */
export function prepareCached(db: Db, sql: string): Statement {
  let byDb = statementCache.get(db);
  if (byDb === undefined) {
    byDb = new Map();
    statementCache.set(db, byDb);
  }
  let stmt = byDb.get(sql);
  if (stmt === undefined) {
    stmt = db.prepare(sql);
    byDb.set(sql, stmt);
  }
  return stmt;
}

export interface OpenDbOptions {
  /**
   * Absolute path to the SQLite file. Defaults to MU_DB_PATH env var or
   * the XDG state path (see `defaultDbPath`). Use a per-test temp path
   * in tests.
   */
  path?: string;

  /**
   * If true, opens the DB read-only. Used by `mu sql` and similar read-only
   * surfaces to enforce no-mutation guarantees at the connection level.
   */
  readonly?: boolean;
}

/**
 * Resolve the canonical DB path:
 *   MU_DB_PATH > <state-dir>/mu.db
 */
export function defaultDbPath(): string {
  if (process.env.MU_DB_PATH) return process.env.MU_DB_PATH;
  return join(defaultStateDir(), "mu.db");
}

/**
 * Open the mu database. Creates the parent directory and applies the schema
 * idempotently when it is missing or differs from this build (an
 * up-to-date DB opens without a write). Safe to call from many short-lived processes
 * concurrently — WAL mode handles cross-process writes.
 */
export function openDb(options: OpenDbOptions = {}): Db {
  const path = options.path ?? defaultDbPath();
  refuseUserDbDuringTests(path);
  mkdirSync(dirname(path), { recursive: true });

  const db = new Database(path, { readonly: options.readonly ?? false });

  if (!options.readonly) {
    // Wait up to 5s for a competing writer's lock instead of throwing
    // SQLITE_BUSY immediately. Every `mu` invocation is a separate
    // short-lived process and a parallel fan-out (`for n in …; do mu
    // agent spawn … & done`) opens the same DB from N processes at
    // once — without this, the losers of a write-lock race die with
    // 'database is locked' and roll back their agent. WAL handles
    // concurrent readers; busy_timeout handles concurrent writers.
    // busy_timeout only covers a lock taken at BEGIN, so every write
    // transaction runs as `db.transaction(fn).immediate()` (or `BEGIN
    // IMMEDIATE`). A deferred one that reads first and then writes gets
    // SQLITE_BUSY_SNAPSHOT at once if another process committed in
    // between, and busy_timeout does not retry that.
    db.pragma("busy_timeout = 5000");
    // Detect schema version BEFORE applySchema so a real v<11 DB is not
    // silently stamped as v11 by the CREATE-IF-NOT-EXISTS in applySchema,
    // and before `journal_mode = WAL`, which rewrites the file header:
    // a refused DB must be left byte-for-byte untouched.
    // One read transaction: a process creating this DB can commit
    // between two bare reads, and "no schema_version table, then a
    // workstreams table" read as a pre-versioning v1 DB.
    const detectedVersion = db.transaction(() => detectExistingSchemaVersion(db))();
    if (detectedVersion !== null && detectedVersion > CURRENT_SCHEMA_VERSION) {
      // A newer mu wrote this DB. Its writes would fail here in
      // confusing ways (unknown columns, constraints this build does not
      // know), so refuse before applySchema touches anything.
      try {
        db.close();
      } catch {
        // best effort
      }
      throw new SchemaTooNewError(detectedVersion, CURRENT_SCHEMA_VERSION);
    }
    if (detectedVersion !== null && detectedVersion < MIN_ACCEPTED_SCHEMA_VERSION) {
      // Loud-fail: refuse to touch a pre-v11 DB. There is no in-place
      // migration to run, so leave the old file untouched and tell the
      // operator how to preserve it (see SchemaTooOldError.errorNextSteps).
      try {
        db.close();
      } catch {
        // best effort
      }
      throw new SchemaTooOldError(detectedVersion, MIN_ACCEPTED_SCHEMA_VERSION);
    }
    setWalMode(db);
    db.pragma("foreign_keys = ON");
    // Skip the DDL write transaction when the DB already matches this
    // build. applySchema takes the write lock and rewrites the views,
    // which bumps the schema cookie on every open: every `mu` call,
    // reads included, queued behind any writer and forced every other
    // connection to re-parse the schema (f_opendb_schema_write).
    if (detectedVersion !== CURRENT_SCHEMA_VERSION || !schemaIsCurrent(db)) applySchema(db);
    seedMachineIdentity(db);
    // Install the op-capture triggers + the _op_ctx temp tables they
    // read. Per-connection, because SQLite forbids a main-schema
    // trigger from referencing the temp schema, so the triggers must
    // themselves be TEMP triggers (see src/capture.ts for the full
    // reasoning and the exact error messages). Must run AFTER
    // seedMachineIdentity: the triggers mint HLCs from that row.
    installCapture(db);
  } else {
    db.pragma("foreign_keys = ON");
  }

  return db;
}

/**
 * Hard guard (Layer "db" of bug_test_flake_round_2): refuse to open
 * the user's REAL default mu.db when running under vitest. Tests
 * MUST point at a per-test temp DB (via MU_DB_PATH or the explicit
 * `{ path }` option). A test that forgets to override either one
 * silently mutated the dev box's live state — we observed a stray
 * 'demo' workstream row replicated from test/tui-acceptance.integration.test.ts
 * into ~/.local/state/mu/mu.db. The guard throws a useful diagnostic
 * the moment the offending openDb() call is made; the failing test's
 * stack trace then names the leak source directly.
 *
 * Test mode = `process.env.VITEST` is defined OR `NODE_ENV === "test"`.
 * vitest sets VITEST="true" in every fork; the NODE_ENV branch is for
 * other runners that may invoke openDb during tests.
 *
 * The user's REAL DB path is computed from HOME / XDG_STATE_HOME
 * directly (NOT from defaultDbPath() — which would honour MU_DB_PATH
 * and produce the temp path the test set, defeating the check).
 * MU_STATE_DIR is deliberately not consulted: test/_setup.ts deletes
 * the user's MU_* vars in every fork, so any MU_STATE_DIR seen here was
 * set by a test to a temp dir (test/disk-recon.test.ts opens
 * <MU_STATE_DIR>/mu.db), and guarding it would refuse that temp DB.
 * Production code paths (the `mu` CLI binary) never set VITEST, so
 * the guard is a complete no-op outside the test runner.
 */
function refuseUserDbDuringTests(path: string): void {
  const inTest = process.env.VITEST !== undefined || process.env.NODE_ENV === "test";
  if (!inTest) return;
  const home = process.env.HOME ?? homedir();
  const realDb = resolve(join(xdgStateHome(home), "mu", "mu.db"));
  if (resolve(path) === realDb) {
    throw new Error(
      `openDb refused: tests must NEVER write to the user DB (${realDb}). Set MU_DB_PATH to a per-test temp path (test/_runCli.ts does this automatically) or pass an explicit { path } argument. The leak source is the call site of openDb in this stack frame.`,
    );
  }
}

// ─── Resolve helpers (operator-facing name -> surrogate id) ───────────
//
// docs/architecture/sdk.md § Surrogate-PK and SDK-boundary discipline:
//
//   PUBLIC SDK functions take operator-facing names (workstream + local
//   id + agent name). Internal helpers take surrogate ids. Resolution
//   happens at the public-function entry, exactly once.
//
// `resolveWorkstreamId` is the only resolve helper that throws a typed
// error from this leaf module — `WorkstreamNotFoundError` is defined
// here, so there is no cycle. The task / agent resolvers RETURN
// `number | null` (`tryResolveTaskId` / `tryResolveAgentId`) and let
// SDK callers in `src/tasks/*.ts` / `src/agents.ts` throw the typed
// `TaskNotFoundError` / `AgentNotFoundError` they own. That keeps
// `cli/handle.ts`'s `instanceof`-based exit-code map (3 = not-found)
// honest: a leaf throwing a plain `Error` whose `.name` was monkey-
// patched to `"TaskNotFoundError"` flunks `instanceof TaskNotFoundError`
// and falls through to the generic exit 1
// (review_substrate_resolve_id_anonymous_errors).

export class WorkstreamNotFoundError extends Error implements HasNextSteps {
  override readonly name = "WorkstreamNotFoundError";
  constructor(public readonly workstream: string) {
    super(`no such workstream: ${workstream}`);
  }
  errorNextSteps(): NextStep[] {
    return [
      { intent: "List workstreams", command: "mu workstream list" },
      {
        intent: "Initialise this workstream",
        command: `mu workstream init ${this.workstream}`,
      },
    ];
  }
}

/** Resolve a workstream name to its INTEGER surrogate id. Throws
 *  WorkstreamNotFoundError on miss. Pure: no auto-create — callers
 *  that want the auto-create-or-resolve semantics use
 *  `ensureWorkstream` from src/workstream.ts (which returns void;
 *  follow up with `resolveWorkstreamId` if the id is needed).
 */
export function resolveWorkstreamId(db: Db, workstream: string): number {
  const row = db.prepare("SELECT id FROM workstreams WHERE name = ?").get(workstream) as
    | { id: number }
    | undefined;
  if (!row) throw new WorkstreamNotFoundError(workstream);
  return row.id;
}

/** Resolve a workstream name to its id, returning null on miss instead
 *  of throwing. Useful for read paths that want to early-return [] on
 *  a non-existent workstream (e.g. listTasks). */
export function tryResolveWorkstreamId(db: Db, workstream: string): number | null {
  const row = db.prepare("SELECT id FROM workstreams WHERE name = ?").get(workstream) as
    | { id: number }
    | undefined;
  return row ? row.id : null;
}

/** Resolve a (workstream_id, local_id) pair to the task's surrogate
 *  id, returning `null` on miss. SDK callers in `src/tasks/*.ts`
 *  wrap the null return in `TaskNotFoundError` so the CLI's typed-
 *  error → exit-code map (3 = not-found) fires. The leaf intentionally
 *  does NOT throw a typed error (it would either pull in a cyclic
 *  import or — as before — fake the `.name` and silently flunk
 *  `instanceof TaskNotFoundError`, falling through to exit 1).
 *  Renamed from `resolveTaskId` in
 *  review_substrate_resolve_id_anonymous_errors. */
export function tryResolveTaskId(db: Db, workstreamId: number, localId: string): number | null {
  const row = db
    .prepare("SELECT id FROM tasks WHERE workstream_id = ? AND local_id = ?")
    .get(workstreamId, localId) as { id: number } | undefined;
  return row ? row.id : null;
}

/** Resolve a (workstream_id, agent_name) pair to the agent's surrogate
 *  id, returning `null` on miss. SDK callers in `src/agents.ts` wrap
 *  the null return in `AgentNotFoundError` so the CLI's typed-error →
 *  exit-code map (3 = not-found) fires. See `tryResolveTaskId` for the
 *  full rationale (review_substrate_resolve_id_anonymous_errors). */
export function tryResolveAgentId(db: Db, workstreamId: number, name: string): number | null {
  const row = db
    .prepare("SELECT id FROM agents WHERE workstream_id = ? AND name = ?")
    .get(workstreamId, name) as { id: number } | undefined;
  return row ? row.id : null;
}

/**
 * Thrown by openDb when the on-disk DB is older than the current
 * schema. There is no in-place migration ladder; the old DB is left
 * untouched. Maps to exit code 4 (conflict) in cli.ts handle().
 */
export class SchemaTooOldError extends Error implements HasNextSteps {
  override readonly name = "SchemaTooOldError";
  constructor(
    public readonly detectedVersion: number,
    public readonly requiredVersion: number,
  ) {
    super(
      `Detected v${detectedVersion} schema; v${requiredVersion} is required. mu ships NO in-place migration — your v${detectedVersion} DB is untouched. Back it up or move it aside, then start a fresh v${requiredVersion} DB.`,
    );
  }
  errorNextSteps(): NextStep[] {
    return [
      {
        intent: "Keep a copy of the old DB before anything else",
        command: `mu db backup "$HOME/mu-v${this.detectedVersion}-backup.db"`,
      },
      {
        intent: "Move the old DB aside without deleting it",
        command: `mv "\${MU_DB_PATH:-$HOME/.local/state/mu/mu.db}" "\${MU_DB_PATH:-$HOME/.local/state/mu/mu.db}.old"`,
      },
      {
        intent: `Migrate the old DB into a fresh v${this.requiredVersion} DB (run in a mu git checkout: the npm package does not ship scripts/)`,
        command: `npx tsx scripts/migrate.ts "\${MU_DB_PATH:-$HOME/.local/state/mu/mu.db}.old" --out /tmp/mu-v${this.requiredVersion}.db`,
      },
      {
        intent: "Verify the fresh DB before swapping",
        command: `MU_DB_PATH=/tmp/mu-v${this.requiredVersion}.db mu doctor --deep`,
      },
    ];
  }
}

/**
 * Thrown by openDb when the on-disk DB was written by a NEWER mu.
 * The DB is left untouched. Maps to exit code 4 (conflict).
 */
export class SchemaTooNewError extends Error implements HasNextSteps {
  override readonly name = "SchemaTooNewError";
  constructor(
    public readonly detectedVersion: number,
    public readonly supportedVersion: number,
  ) {
    super(`DB is v${detectedVersion}; this mu understands up to v${supportedVersion}. Upgrade mu.`);
  }
  errorNextSteps(): NextStep[] {
    return [{ intent: "Check which mu is on PATH", command: "which mu && mu --version" }];
  }
}

/** How long openDb retries the switch to WAL while another process holds the lock. */
const WAL_SWITCH_RETRY_MS = 5000;

/**
 * `journal_mode = WAL`, retried on SQLITE_BUSY. On a fresh file the
 * switch needs an exclusive lock, and SQLite can return SQLITE_BUSY
 * for it without calling the busy handler, so busy_timeout does not
 * cover it: N processes opening a new DB at once lost one with
 * "database is locked". Once the file is WAL the switch is a no-op.
 */
function setWalMode(db: Db): void {
  const deadline = Date.now() + WAL_SWITCH_RETRY_MS;
  const pause = new Int32Array(new SharedArrayBuffer(4));
  for (;;) {
    try {
      db.pragma("journal_mode = WAL");
      return;
    } catch (err) {
      const code = (err as { code?: unknown }).code;
      const busy = typeof code === "string" && code.startsWith("SQLITE_BUSY");
      if (!busy || Date.now() >= deadline) throw err;
      Atomics.wait(pause, 0, 0, 10 + Math.floor(Math.random() * 20));
    }
  }
}

/**
 * Sniff an existing DB's schema version BEFORE applySchema runs, so we
 * can distinguish:
 *   - Brand-new DB: no tables at all -> returns null (fresh, will be
 *     stamped to CURRENT_SCHEMA_VERSION by applySchema).
 *   - Pre-versioning DB (had the original tables before schema_version existed):
 *     workstreams exists, schema_version doesn't -> returns 1.
 *   - Already-versioned DB: schema_version row present -> returns its
 *     value.
 */
function detectExistingSchemaVersion(db: Db): number | null {
  const hasVersionTable = db
    .prepare("SELECT name FROM sqlite_master WHERE type='table' AND name='schema_version'")
    .get() as { name: string } | undefined;
  if (hasVersionTable) {
    const row = db.prepare("SELECT version FROM schema_version WHERE id = 1").get() as
      | { version: number }
      | undefined;
    return row?.version ?? null;
  }
  // No schema_version table. Check whether any of the original
  // tables exist; if so this is a pre-versioning DB.
  const hasWorkstreams = db
    .prepare("SELECT name FROM sqlite_master WHERE type='table' AND name='workstreams'")
    .get() as { name: string } | undefined;
  if (hasWorkstreams) return 1;
  return null;
}

/** Seed the singleton machine_identity row (id=1) on first open.
 *  No-op once a row exists. The machine_id it writes is the identity
 *  every op is stamped with (`ops.machine_id`) and the name of this
 *  machine's segment. */
function seedMachineIdentity(db: Db): void {
  const row = db.prepare("SELECT COUNT(*) AS count FROM machine_identity").get() as {
    count: number;
  };
  if (row.count !== 0) return;
  db.prepare(
    `INSERT OR IGNORE INTO machine_identity (id, machine_id, hostname, created_at)
     VALUES (1, ?, ?, ?)`,
  ).run(randomUUID(), hostname(), new Date().toISOString());
}

/**
 * Apply the schema. Idempotent: tables use CREATE TABLE IF NOT EXISTS;
 * views are dropped and recreated so the latest definition always wins.
 *
 * For fresh DBs this writes the current schema shape and stamps
 * schema_version = CURRENT_SCHEMA_VERSION. For existing v11 DBs this is
 * a no-op for the table CREATEs (IF NOT EXISTS) but DOES recreate the
 * views. Pre-v11 DBs never reach this function — openDb's loud-fail
 * hook rejects them with SchemaTooOldError first.
 *
 * There is no in-place bump ladder. MIN_ACCEPTED === CURRENT, so the
 * only two shapes that reach here are "brand new" and "already v11";
 * any older-DB fix-up code would be dead by construction.
 */
function applySchema(db: Db): void {
  // Apply the schema DDL atomically. CURRENT_SCHEMA includes
  // `DROP VIEW IF EXISTS goals; CREATE VIEW goals …` (views can't be
  // CREATE-IF-NOT-EXISTS + redefined in one step), so without a
  // transaction two processes opening the same fresh DB concurrently
  // can interleave one's DROP between the other's DROP and CREATE and
  // hit 'view goals already exists'. An IMMEDIATE transaction takes the
  // write lock up front (paired with busy_timeout in openDb) so the
  // whole DDL block is all-or-nothing per process.
  // bug_parallel_spawn_races_drop_agents (schema leg).
  db.exec("BEGIN IMMEDIATE");
  try {
    db.exec(CURRENT_SCHEMA);
    // Seed the legal (status, substate) pairs from code. INSERT OR
    // IGNORE keeps it idempotent; the table is machine-local and never
    // synced, so every machine derives it from the same constant.
    const seed = db.prepare(
      "INSERT OR IGNORE INTO task_substates (status, substate, is_default) VALUES (?, ?, ?)",
    );
    for (const [status, substate, isDefault] of TASK_SUBSTATE_ROWS) {
      seed.run(status, substate, isDefault);
    }
    db.exec("COMMIT");
  } catch (err) {
    db.exec("ROLLBACK");
    // A concurrent process already applied the (idempotent) schema —
    // the views/tables now exist. Re-throwing would fail the spawn for
    // a benign race, so swallow the 'already exists' class and proceed;
    // any other error is a real problem and rethrows.
    const msg = err instanceof Error ? err.message : String(err);
    if (!/already exists/i.test(msg)) throw err;
  }
  // Stamp the version on a fresh DB. INSERT OR IGNORE so we don't
  // overwrite the version on an existing v11 DB.
  db.prepare("INSERT OR IGNORE INTO schema_version (id, version) VALUES (1, ?)").run(
    CURRENT_SCHEMA_VERSION,
  );
}

/** Names CURRENT_SCHEMA creates with IF NOT EXISTS, and the exact SQL
 *  SQLite stores for each view (the CREATE statement minus its `;`). */
interface ExpectedObjects {
  tables: string[];
  indexes: string[];
  views: Map<string, string>;
}
let expectedCache: ExpectedObjects | undefined;
function expectedObjects(): ExpectedObjects {
  if (expectedCache !== undefined) return expectedCache;
  const names = (re: RegExp): string[] =>
    [...CURRENT_SCHEMA.matchAll(re)].map((m) => m[1]).filter((n) => n !== undefined);
  const views = new Map<string, string>();
  for (const sql of [READY_VIEW_SQL, BLOCKED_VIEW_SQL, GOALS_VIEW_SQL]) {
    const m = /CREATE VIEW (\w+)[^;]*/.exec(sql);
    if (m?.[1] !== undefined) views.set(m[1], m[0]);
  }
  expectedCache = {
    tables: names(/CREATE TABLE IF NOT EXISTS (\w+)/g),
    indexes: names(/CREATE (?:UNIQUE )?INDEX IF NOT EXISTS (\w+)/g),
    views,
  };
  return expectedCache;
}

/** True when applySchema would change nothing: every table, index and
 *  seeded substate row exists, and every view's stored SQL equals this
 *  build's definition. Reads only, so openDb on an up-to-date DB takes
 *  no write lock and leaves PRAGMA schema_version alone. */
function schemaIsCurrent(db: Db): boolean {
  const rows = db.prepare("SELECT type, name, sql FROM sqlite_master").all() as {
    type: string;
    name: string;
    sql: string | null;
  }[];
  const have = new Map(rows.map((r) => [`${r.type}:${r.name}`, r.sql]));
  const expected = expectedObjects();
  if (expected.tables.some((t) => !have.has(`table:${t}`))) return false;
  if (expected.indexes.some((i) => !have.has(`index:${i}`))) return false;
  for (const [name, sql] of expected.views) {
    if (have.get(`view:${name}`) !== sql) return false;
  }
  const pairs = new Set(
    (
      db.prepare("SELECT status, substate FROM task_substates").all() as {
        status: string;
        substate: string;
      }[]
    ).map((r) => `${r.status}/${r.substate}`),
  );
  return TASK_SUBSTATE_ROWS.every(([status, substate]) => pairs.has(`${status}/${substate}`));
}

/** The schema version a fresh DB starts at. v11 adds tasks.substate
 *  and the task_substates lookup table on top of v10's three-state
 *  lifecycle (OPEN, IN_PROGRESS, CLOSED). See CHANGELOG.md. */
export const CURRENT_SCHEMA_VERSION = 11;

/** The lowest schema version `openDb` will accept. Equal to
 *  CURRENT_SCHEMA_VERSION: openDb runs no migration, so every pre-v11 DB
 *  throws `SchemaTooOldError` (exit 4) and is left untouched on disk. */
const MIN_ACCEPTED_SCHEMA_VERSION = 11;

/** Tables a healthy DB must contain. Single source of truth so
 *  `mu doctor` and any other consumer don't drift. Adding a new table
 *  = one new entry here AND a CREATE TABLE in CURRENT_SCHEMA, plus a
 *  CURRENT_SCHEMA_VERSION bump (sync_fingerprints went in without one:
 *  additive, and an older mu ignores it). Sorted; exactly 12 entries. */
export const EXPECTED_TABLES: readonly string[] = [
  "agents",
  "machine_identity",
  "ops",
  "schema_version",
  "sync_fingerprints",
  "sync_peers",
  "task_edges",
  "task_notes",
  "task_substates",
  "tasks",
  "vcs_workspaces",
  "workstreams",
];

// ─── Syncability — which state crosses machines ───────────────────────
//
// Whether an op syncs is a STATIC function of its entity, so it lives
// here as a constant rather than as a `local` column on `ops`. A
// per-row decision is a per-row opportunity to get it wrong, and it
// would cost a column plus a branch at every write site. Downstream
// sync code READS these; it does not re-decide.
//
// Vocabulary (docs/VOCABULARY.md § portable): **portable** tables are
// syncable across machines; **machine-local** tables never leave the
// box. Machine-local ops are still RECORDED — `mu log` and the TUI
// Recent card still show agent spawn/close — they simply never ship.
// "Not synced" is not "not logged".

/** Op entities that cross machines. Everything else is machine-local.
 *  Readonly tuple (not `string[]`) so `SyncedEntity` is a real union
 *  and downstream code gets compile-time checking, not raw strings. */
export const SYNCED_ENTITIES = ["workstream", "task", "edge", "note", "message"] as const;

/** One of the op entities that sync. Derived from the tuple, so
 *  adding an entity is a one-line change with no type to keep in step. */
export type SyncedEntity = (typeof SYNCED_ENTITIES)[number];

/** Op entities this build KNOWS are machine-local: their payloads name
 *  a pane id or an absolute path, so a peer sending one is a real bug
 *  and `applyOp` rejects it loudly.
 *
 *  WHY THIS LIST EXISTS SEPARATELY FROM "not in SYNCED_ENTITIES"
 *  ------------------------------------------------------------
 *  "Unknown" and "known-local" are different failures and were
 *  conflated, which wedged a real fleet. A peer running a LATER (or
 *  earlier) mu wrote `entity:"marker"` ops — legal on the writer, whose
 *  SYNCED_ENTITIES included it. The reader treated every non-synced
 *  entity as a bad peer, so `ingestSegment` recorded a defect and
 *  stopped at that line FOREVER: 87% of a 20,305-line segment never
 *  applied, and `mu sync --repair` (which only resets the watermark)
 *  marched straight back into the same wall.
 *
 *  So the rule is asymmetric on purpose: an entity we know must never
 *  travel is a defect; an entity we simply do not recognise is tolerated
 *  forward-compatibly — recorded in `ops`, projected nowhere, exactly
 *  like 'message'. Reader vocabulary may lag writer vocabulary; that is
 *  a fact of a mixed fleet, not a corruption. */
export const MACHINE_LOCAL_ENTITIES = ["agent", "workspace", "event", "broadcast"] as const;

export type MachineLocalEntity = (typeof MACHINE_LOCAL_ENTITIES)[number];

/** **Portable** tables: their rows mean the same thing on any machine,
 *  so their ops ship. Mirrors docs/VOCABULARY.md § portable exactly. */
export const PORTABLE_TABLES = ["task_edges", "task_notes", "tasks", "workstreams"] as const;

export type PortableTable = (typeof PORTABLE_TABLES)[number];

/** **Machine-local** tables. Never bulk-copied to a peer, because a
 *  row's meaning does not survive the trip:
 *
 *    agents           holds `pane_id` ('%17') — meaningless elsewhere.
 *    vcs_workspaces   holds absolute paths — /home/... vs /Users/...
 *                     on a mixed macOS/Linux fleet.
 *    machine_identity IS the per-machine identity.
 *    schema_version   local bookkeeping.
 *    sync_peers       local bookkeeping (per-peer watermarks).
 *    sync_fingerprints local cache of what the last clean ingest saw.
 *    task_substates   seeded identically on every machine from
 *                     TASK_SUBSTATE_ROWS; code, not data.
 *    ops              see below — the carrier, not cargo.
 *
 *  `ops` is listed here deliberately rather than omitted. It is not
 *  **portable** in the copy-the-table sense: the table is never
 *  wholesale-copied. Individual op ROWS ship, one at a time, filtered
 *  by SYNCED_ENTITIES and carried by per-machine **segments** — and
 *  `seq` is a local-only append cursor that means nothing on a peer.
 *  So for the only question this list answers — "is this table's
 *  content copied across machines?" — the answer for `ops` is no.
 *
 *  Consequence that falls out with no special case: `tasks.owner_id`
 *  is an FK into `agents`, and `agents` is machine-local. Therefore
 *  OWNERSHIP DOES NOT SYNC. The deleted db-sync.ts reached the same
 *  conclusion via an `includeOwners` flag; here it is structural. */
export const MACHINE_LOCAL_TABLES = [
  "agents",
  "machine_identity",
  "ops",
  "schema_version",
  "sync_fingerprints",
  "sync_peers",
  "task_substates",
  "vcs_workspaces",
] as const;

export type MachineLocalTable = (typeof MACHINE_LOCAL_TABLES)[number];

// ─── View DDL — single source of truth ────────────────────────────────
//
// The three views (ready, blocked, goals) get DROPped + CREATEd by
// applySchema whenever openDb finds them missing or different from
// these definitions (schemaIsCurrent). Each constant is self-contained:
// DROP IF EXISTS + CREATE. Running DROP twice in a row is harmless,
// so callers that already DROP up-front can still re-execute these
// without churn.
//
// Exported as named constants so consumers can reference the canonical
// shape (e.g. one-shot migration scripts under scripts/) without
// duplicating SQL.

export const READY_VIEW_SQL = `
DROP VIEW IF EXISTS ready;
CREATE VIEW ready AS
  SELECT t.*
    FROM tasks t
   WHERE t.status = 'OPEN'
     AND t.substate NOT IN ('parked', 'triage')
     AND NOT EXISTS (
       SELECT 1
         FROM task_edges e
         JOIN tasks      b ON e.from_task_id = b.id
        WHERE e.to_task_id = t.id
          AND b.status <> 'CLOSED'
     );
`;

export const BLOCKED_VIEW_SQL = `
DROP VIEW IF EXISTS blocked;
CREATE VIEW blocked AS
  SELECT t.*
    FROM tasks t
   WHERE t.status = 'OPEN'
     AND EXISTS (
       SELECT 1
         FROM task_edges e
         JOIN tasks      b ON e.from_task_id = b.id
        WHERE e.to_task_id = t.id
          AND b.status <> 'CLOSED'
     );
`;

// A goal is an active endpoint of the DAG — a task with no dependents
// that we're still working toward. CLOSED is excluded: a finished
// leaf is not an active goal. Parked tasks STAY goals: tracks are
// built from goals, and a parked track is still a track.
export const GOALS_VIEW_SQL = `
DROP VIEW IF EXISTS goals;
CREATE VIEW goals AS
  SELECT t.*
    FROM tasks t
   WHERE t.status <> 'CLOSED'
     AND NOT EXISTS (
       SELECT 1 FROM task_edges WHERE from_task_id = t.id
     );
`;

// ─── v11 SCHEMA ───────────────────────────────────────────────────────
//
// Per docs/architecture/sdk.md § Surrogate-PK and SDK-boundary discipline.
// Every entity table has:
//   - INTEGER PRIMARY KEY AUTOINCREMENT (surrogate identity)
//   - <scope>_id INTEGER NOT NULL REFERENCES <parent>(id) ON DELETE CASCADE
//   - <name>     TEXT  NOT NULL  (operator-facing, mutable)
//   - UNIQUE (<scope>_id, <name>)
//
// Foreign keys are INTEGER. Renames become single-row UPDATEs (no
// cascade chain). The TEXT name is just an attribute. `ops` is the
// documented exception: it has NO foreign keys at all and addresses
// rows by their NATURAL key, because ops outlive the rows (and the
// workstreams) they record.

const CURRENT_SCHEMA = `
-- ─── Schema versioning ────────────────────────────────────────────────
--
-- Single-row table tracking which schema version this DB is at. Migrations
-- read and update this; the row is INSERT-OR-IGNOREd by applySchema with
-- the current version on a fresh DB.
CREATE TABLE IF NOT EXISTS schema_version (
  id      INTEGER PRIMARY KEY CHECK (id = 1),
  version INTEGER NOT NULL
);

-- machine_identity: one durable identity per DB/machine, seeded by
-- openDb after schema creation. hostname is advisory only.
--
-- last_wall / last_counter are the persisted hybrid logical clock
-- (src/hlc.ts, VOCABULARY § HLC). They live here because every mu
-- invocation is a fresh process — an in-memory counter would reset
-- constantly and mint duplicate HLCs, which UNIQUE (machine_id, hlc)
-- on the ops table would then reject.
CREATE TABLE IF NOT EXISTS machine_identity (
  id           INTEGER PRIMARY KEY CHECK (id = 1),
  machine_id   TEXT NOT NULL,
  hostname     TEXT,
  created_at   TEXT NOT NULL,
  last_wall    INTEGER NOT NULL DEFAULT 0,
  last_counter INTEGER NOT NULL DEFAULT 0
);

-- ─── Tables ───────────────────────────────────────────────────────────

-- workstreams: top of the hierarchy. name stays globally unique
-- because it IS a tmux session name; no <scope_id> column because
-- there's no parent.
CREATE TABLE IF NOT EXISTS workstreams (
  id          INTEGER PRIMARY KEY AUTOINCREMENT,
  name        TEXT UNIQUE NOT NULL,
  created_at  TEXT NOT NULL                  -- ISO 8601
);


-- agents: one row per managed pane. Per-workstream unique on name.
CREATE TABLE IF NOT EXISTS agents (
  id            INTEGER PRIMARY KEY AUTOINCREMENT,
  workstream_id INTEGER NOT NULL REFERENCES workstreams (id) ON DELETE CASCADE,
  name          TEXT NOT NULL,                  -- per-workstream unique
  cli           TEXT NOT NULL DEFAULT 'pi',
  pane_id       TEXT NOT NULL,
  status        TEXT NOT NULL,                    -- DEPRECATED (2.0): always 'spawning'; runtime state lives in murmur. Drop at the first v11 migration.
  role          TEXT NOT NULL DEFAULT 'full-access',
  tab           TEXT,
  created_at    TEXT NOT NULL,
  updated_at    TEXT NOT NULL,
  UNIQUE (workstream_id, name),
  CHECK (status IN (
    'spawning', 'busy', 'needs_input', 'needs_permission',
    'free', 'unreachable', 'terminated'
  )),
  CHECK (role IN ('full-access', 'read-only'))
);

CREATE INDEX IF NOT EXISTS idx_agents_workstream ON agents (workstream_id);
CREATE INDEX IF NOT EXISTS idx_agents_status ON agents (status);

-- task_substates: the legal (status, substate) pairs. Seeded from
-- TASK_SUBSTATE_ROWS (src/tasks/status.ts) by applySchema; exactly one
-- default per status.
CREATE TABLE IF NOT EXISTS task_substates (
  status     TEXT NOT NULL,
  substate   TEXT NOT NULL,
  is_default INTEGER NOT NULL DEFAULT 0 CHECK (is_default IN (0, 1)),
  PRIMARY KEY (status, substate)
);

CREATE UNIQUE INDEX IF NOT EXISTS idx_task_substates_one_default
  ON task_substates (status) WHERE is_default = 1;

-- tasks: per-workstream unique on local_id (TRULY local now —
-- different workstreams may reuse the same local_id).
CREATE TABLE IF NOT EXISTS tasks (
  id            INTEGER PRIMARY KEY AUTOINCREMENT,
  workstream_id INTEGER NOT NULL REFERENCES workstreams (id) ON DELETE CASCADE,
  local_id      TEXT NOT NULL,                 -- per-workstream unique
  title         TEXT NOT NULL,
  status        TEXT NOT NULL DEFAULT 'OPEN',
  -- OPEN | IN_PROGRESS | CLOSED — see VOCABULARY.md.
  substate      TEXT NOT NULL,
  -- Qualifies status. No DEFAULT on purpose: a writer that forgets it
  -- must fail loudly rather than silently pick one.
  impact        INTEGER NOT NULL,
  effort_days   REAL NOT NULL,
  owner_id      INTEGER REFERENCES agents (id) ON DELETE SET NULL,
  created_at    TEXT NOT NULL,
  updated_at    TEXT NOT NULL,
  UNIQUE (workstream_id, local_id),
  CHECK (impact BETWEEN 1 AND 100),
  CHECK (effort_days > 0),
  FOREIGN KEY (status, substate) REFERENCES task_substates (status, substate)
    DEFERRABLE INITIALLY DEFERRED
);

CREATE INDEX IF NOT EXISTS idx_tasks_workstream ON tasks (workstream_id);
CREATE INDEX IF NOT EXISTS idx_tasks_status ON tasks (status);
CREATE INDEX IF NOT EXISTS idx_tasks_owner ON tasks (owner_id);

-- task_edges: composite PK by pair. INTEGER FKs into tasks.id.
CREATE TABLE IF NOT EXISTS task_edges (
  from_task_id INTEGER NOT NULL REFERENCES tasks (id) ON DELETE CASCADE,
  to_task_id   INTEGER NOT NULL REFERENCES tasks (id) ON DELETE CASCADE,
  created_at   TEXT NOT NULL,
  PRIMARY KEY (from_task_id, to_task_id),
  CHECK (from_task_id <> to_task_id)
);

CREATE INDEX IF NOT EXISTS idx_task_edges_to ON task_edges (to_task_id);

-- task_notes: append-only context. author stays free-text
-- ("orchestrator", "user", "π - mu", "system") — not always a
-- registered agent.
CREATE TABLE IF NOT EXISTS task_notes (
  id         INTEGER PRIMARY KEY AUTOINCREMENT,
  task_id    INTEGER NOT NULL REFERENCES tasks (id) ON DELETE CASCADE,
  author     TEXT,
  content    TEXT NOT NULL,
  created_at TEXT NOT NULL
);

CREATE INDEX IF NOT EXISTS idx_task_notes_task ON task_notes (task_id);

-- ─── The ops log (VISION.md § 2b, VOCABULARY.md § op / ops log) ──────
--
-- The single append-only record of every change. Sync, undo, and
-- history are all queries or replays over this one table.
--
-- Deliberately FK-free. An op must stay readable after the row (and
-- the workstream) it records is gone — a tombstone op for a destroyed
-- workstream is the whole point — so 'key' holds the NATURAL key
-- ('<workstream>/<local_id>'), never a surrogate id. That is also why
-- keys don't collide across machines.
--
--   seq        local-only append cursor (AUTOINCREMENT never recycles)
--   hlc        the ORDERING key across machines (VOCABULARY § HLC)
--   machine_id which peer wrote this op
--   group_id   the undo unit: all ops of one operator action
--   actor      who caused it (may not be a registered worker)
--   intent     semantic label ('task.close', 'agent.spawn')
--   entity/key what it addresses ('task', 'mu/ops-log')
--   op         'put' (semantic partial update) | 'del' (tombstone)
--   payload    JSON of ONLY the columns that changed
--
-- UNIQUE (machine_id, hlc) is load-bearing: it makes ingest idempotent
-- for free, which is what lets 'mu sync --repair' be nothing more than
-- "re-read that peer's segment from zero".
CREATE TABLE IF NOT EXISTS ops (
  seq        INTEGER PRIMARY KEY AUTOINCREMENT,
  hlc        TEXT NOT NULL,
  machine_id TEXT NOT NULL,
  group_id   TEXT NOT NULL,
  actor      TEXT,
  intent     TEXT,
  entity     TEXT NOT NULL,
  key        TEXT NOT NULL,
  op         TEXT NOT NULL CHECK (op IN ('put','del')),
  payload    TEXT NOT NULL,
  created_at TEXT NOT NULL,
  UNIQUE (machine_id, hlc)
);

-- Four indexes, one per read shape that actually exists:
--   hlc         — replay/rebuild walks the log in HLC order.
--   entity,key  — "what happened to this task" (history + per-field merge).
--   group_id    — 'mu undo <group>' gathers one action's ops.
--   machine_id,entity,hlc — sync flush seeks this machine's SYNCED ops
--                 past the segment's last hlc, so a trailing run of
--                 machine-local ops (agent.*) costs nothing per flush.
-- (Other machine_id lookups ride the UNIQUE (machine_id, hlc) index;
-- seq is the PK. No speculative index beyond these.)
CREATE INDEX IF NOT EXISTS idx_ops_hlc ON ops (hlc);
CREATE INDEX IF NOT EXISTS idx_ops_entity_key ON ops (entity, key);
CREATE INDEX IF NOT EXISTS idx_ops_group ON ops (group_id);
CREATE INDEX IF NOT EXISTS idx_ops_machine_entity_hlc ON ops (machine_id, entity, hlc);

-- sync_peers: one row per known peer, holding its watermark — how far
-- into that peer's segment we have applied. One integer suffices
-- because segments are append-only and ordered. Rows are created on
-- demand at first ingest; there is no membership list to configure.
-- A Syncthing conflict copy gets its own row, keyed by its file stem.
-- last_seen_at is the ISO time of the last watermark write.
CREATE TABLE IF NOT EXISTS sync_peers (
  machine_id       TEXT PRIMARY KEY,
  last_applied_seq INTEGER NOT NULL DEFAULT 0,
  last_seen_at     TEXT
);

-- sync_fingerprints: the caught-up fingerprint, '<size> <mtimeMs>
-- <manifest sha256>' of a peer segment, recorded with the watermark
-- when ingest read that segment cleanly to its end (src/segments.ts
-- segmentCaughtUp). Keyed like sync_peers; a watermark write without a
-- clean read deletes the row. A cache: losing it costs one full read.
-- The value may carry tab-separated resume fields after the key
-- (src/segment-resume.ts): byte offset and sha256 state for ingest
-- to read only appended bytes.
-- Additive, so no schema bump: an older mu ignores it, and any
-- watermark it writes can no longer match a stale fingerprint.
CREATE TABLE IF NOT EXISTS sync_fingerprints (
  machine_id  TEXT PRIMARY KEY,
  fingerprint TEXT NOT NULL
);

-- vcs_workspaces: one isolated working copy per agent.
-- UNIQUE (agent_id) enforces the 1:1 invariant; workstream_id is
-- denormalised for query convenience. path is UNIQUE because two
-- agents pointing at the same on-disk workspace would defeat the
-- purpose.
CREATE TABLE IF NOT EXISTS vcs_workspaces (
  id            INTEGER PRIMARY KEY AUTOINCREMENT,
  agent_id      INTEGER NOT NULL UNIQUE REFERENCES agents (id) ON DELETE CASCADE,
  workstream_id INTEGER NOT NULL REFERENCES workstreams (id) ON DELETE CASCADE,
  backend       TEXT NOT NULL CHECK (backend IN ('jj', 'sl', 'git', 'none')),
  path          TEXT NOT NULL UNIQUE,
  parent_ref    TEXT,
  created_at    TEXT NOT NULL
);

CREATE INDEX IF NOT EXISTS idx_vcs_workspaces_workstream ON vcs_workspaces (workstream_id);

-- ─── Views (replaced when stale so the latest definition wins) ────────
-- See READY_VIEW_SQL / BLOCKED_VIEW_SQL / GOALS_VIEW_SQL above for the
-- canonical DDL — interpolated here so applySchema is one db.exec().
${READY_VIEW_SQL}
${BLOCKED_VIEW_SQL}
${GOALS_VIEW_SQL}
`;
